import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CategorySelectorModal } from './CategorySelectorModal';
import ModalCloseButton from './ModalCloseButton';
import '../styles/components/bulkMoveModal.css';

interface BulkEditModalProps {
  open: boolean;
  itemCount: number;
  onClose: () => void;
  onConfirm: (data: {
    area: string;
    location: string;
    subLocation: string;
    categoryIds: number[];
    quantity?: number;
    purchaseDate?: string;
    // Clear flags - when true, explicitly clear the field
    clearArea?: boolean;
    clearLocation?: boolean;
    clearSubLocation?: boolean;
    clearCategories?: boolean;
    clearQuantity?: boolean;
    clearPurchaseDate?: boolean;
    spaceId?: number;
  }) => void;
  token: string;
  allCategories: any[];
  spaces: any[];
}

const BulkEditModal: React.FC<BulkEditModalProps> = ({
  open,
  itemCount,
  onClose,
  onConfirm,
  token,
  allCategories,
  spaces,
}) => {
  const [area, setArea] = useState('');
  const [location, setLocation] = useState('');
  const [subLocation, setSubLocation] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [quantity, setQuantity] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [selectedSpaceId, setSelectedSpaceId] = useState<number | ''>('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  
  // Clear flags - when checked, explicitly clear the field value
  const [clearArea, setClearArea] = useState(false);
  const [clearLocation, setClearLocation] = useState(false);
  const [clearSubLocation, setClearSubLocation] = useState(false);
  const [clearCategories, setClearCategories] = useState(false);
  const [clearQuantity, setClearQuantity] = useState(false);
  const [clearPurchaseDate, setClearPurchaseDate] = useState(false);

  if (!open) return null;

  const hasChanges = area.trim() || location.trim() || subLocation.trim() || 
                     selectedCategoryIds.length > 0 || quantity.trim() || purchaseDate ||
                     clearArea || clearLocation || clearSubLocation || 
                     clearCategories || clearQuantity || clearPurchaseDate ||
                     selectedSpaceId !== '';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (hasChanges) {
      onConfirm({
        area: area.trim(),
        location: location.trim(),
        subLocation: subLocation.trim(),
        categoryIds: selectedCategoryIds,
        quantity: quantity.trim() ? parseInt(quantity.trim(), 10) : undefined,
        purchaseDate: purchaseDate || undefined,
        clearArea,
        clearLocation,
        clearSubLocation,
        clearCategories,
        clearQuantity,
        clearPurchaseDate,
        spaceId: selectedSpaceId !== '' ? selectedSpaceId : undefined,
      });
      setArea('');
      setLocation('');
      setSubLocation('');
      setSelectedCategoryIds([]);
      setQuantity('');
      setPurchaseDate('');
      setSelectedSpaceId('');
      setClearArea(false);
      setClearLocation(false);
      setClearSubLocation(false);
      setClearCategories(false);
      setClearQuantity(false);
      setClearPurchaseDate(false);
    }
  };

  const handleClose = () => {
    setArea('');
    setLocation('');
    setSubLocation('');
    setSelectedCategoryIds([]);
    setQuantity('');
    setPurchaseDate('');
    setSelectedSpaceId('');
    setClearArea(false);
    setClearLocation(false);
    setClearSubLocation(false);
    setClearCategories(false);
    setClearQuantity(false);
    setClearPurchaseDate(false);
    onClose();
  };

  return createPortal(
    <>
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-container bulk-move-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Changes Will Affect {itemCount} Items</h3>
            <ModalCloseButton onClick={handleClose} />
          </div>

          <form onSubmit={handleSubmit} className="modal-body bulk-move-form">
            {/* Categories */}
            <div className="bulk-move-field">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ 
                  fontSize: 'var(--font-size-sm)', 
                  fontWeight: 500, 
                  color: 'var(--color-text-secondary)',
                  textAlign: 'left'
                }}>
                  Category:
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', cursor: 'pointer' }}>
                  <input 
                    type="checkbox" 
                    checked={clearCategories} 
                    onChange={(e) => {
                      setClearCategories(e.target.checked);
                      if (e.target.checked) setSelectedCategoryIds([]);
                    }}
                    style={{ width: '14px', height: '14px', accentColor: 'var(--color-danger)' }}
                  />
                  Clear
                </label>
              </div>
              <button
                type="button"
                className="input-standard"
                onClick={() => !clearCategories && setShowCategoryModal(true)}
                disabled={clearCategories}
                style={{
                  textAlign: 'left',
                  cursor: clearCategories ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  opacity: clearCategories ? 0.5 : 1
                }}
              >
                <span style={{ 
                  color: clearCategories ? 'var(--color-danger)' : (selectedCategoryIds.length > 0 ? 'var(--color-text-primary)' : 'var(--color-text-muted)'),
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1
                }}>
                  {clearCategories 
                    ? 'Will be cleared'
                    : (selectedCategoryIds.length > 0 
                      ? allCategories
                          .filter(cat => selectedCategoryIds.includes(cat.id))
                          .map(cat => cat.label)
                          .join(', ')
                      : 'Select categories...')}
                </span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginLeft: '8px' }}>
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
            </div>

            <div className="bulk-move-field">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--color-text-secondary)' }}>Area:</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={clearArea} onChange={(e) => { setClearArea(e.target.checked); if (e.target.checked) setArea(''); }} style={{ width: '14px', height: '14px', accentColor: 'var(--color-danger)' }} />
                  Clear
                </label>
              </div>
              <input
                id="bulk-area"
                type="text"
                className="input-standard"
                value={clearArea ? '' : area}
                onChange={(e) => setArea(e.target.value)}
                placeholder={clearArea ? 'Will be cleared' : 'Area'}
                disabled={clearArea}
                style={{ opacity: clearArea ? 0.5 : 1, color: clearArea ? 'var(--color-danger)' : undefined }}
              />
            </div>

            <div className="bulk-move-field">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--color-text-secondary)' }}>Location:</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={clearLocation} onChange={(e) => { setClearLocation(e.target.checked); if (e.target.checked) setLocation(''); }} style={{ width: '14px', height: '14px', accentColor: 'var(--color-danger)' }} />
                  Clear
                </label>
              </div>
              <input
                id="bulk-location"
                type="text"
                className="input-standard"
                value={clearLocation ? '' : location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder={clearLocation ? 'Will be cleared' : 'Location'}
                disabled={clearLocation}
                style={{ opacity: clearLocation ? 0.5 : 1, color: clearLocation ? 'var(--color-danger)' : undefined }}
              />
            </div>

            <div className="bulk-move-field">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--color-text-secondary)' }}>Sub-location:</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={clearSubLocation} onChange={(e) => { setClearSubLocation(e.target.checked); if (e.target.checked) setSubLocation(''); }} style={{ width: '14px', height: '14px', accentColor: 'var(--color-danger)' }} />
                  Clear
                </label>
              </div>
              <input
                id="bulk-sub-location"
                type="text"
                className="input-standard"
                value={clearSubLocation ? '' : subLocation}
                onChange={(e) => setSubLocation(e.target.value)}
                placeholder={clearSubLocation ? 'Will be cleared' : 'Sub-location'}
                disabled={clearSubLocation}
                style={{ opacity: clearSubLocation ? 0.5 : 1, color: clearSubLocation ? 'var(--color-danger)' : undefined }}
              />
            </div>

            <div className="bulk-move-field">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--color-text-secondary)' }}>Quantity:</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={clearQuantity} onChange={(e) => { setClearQuantity(e.target.checked); if (e.target.checked) setQuantity(''); }} style={{ width: '14px', height: '14px', accentColor: 'var(--color-danger)' }} />
                  Clear
                </label>
              </div>
              <input
                id="bulk-quantity"
                type="number"
                min="0"
                className="input-standard"
                value={clearQuantity ? '' : quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder={clearQuantity ? 'Will be cleared' : 'Quantity'}
                disabled={clearQuantity}
                style={{ opacity: clearQuantity ? 0.5 : 1, color: clearQuantity ? 'var(--color-danger)' : undefined }}
              />
            </div>

            <div className="bulk-move-field">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--color-text-secondary)' }}>Purchase Date:</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={clearPurchaseDate} onChange={(e) => { setClearPurchaseDate(e.target.checked); if (e.target.checked) setPurchaseDate(''); }} style={{ width: '14px', height: '14px', accentColor: 'var(--color-danger)' }} />
                  Clear
                </label>
              </div>
              <input
                id="bulk-purchase-date"
                type="date"
                className="input-standard"
                value={clearPurchaseDate ? '' : purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                disabled={clearPurchaseDate}
                style={{ opacity: clearPurchaseDate ? 0.5 : 1, color: clearPurchaseDate ? 'var(--color-danger)' : undefined }}
              />
            </div>

            <div className="bulk-move-field">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--color-text-secondary)' }}>Move to Space:</label>
              </div>
              <select
                className="input-standard"
                value={selectedSpaceId}
                onChange={(e) => setSelectedSpaceId(e.target.value ? Number(e.target.value) : '')}
                style={{
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 10px center',
                  paddingRight: '28px',
                }}
              >
                <option value="">(Keep current space)</option>
                {spaces.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div className="bulk-move-hint">
              Leave fields empty to keep current values. Check "Clear" to remove existing values.
            </div>
          </form>

          <div className="modal-footer">
            <button type="button" className="modal-btn-secondary bulk-move-btn-cancel" onClick={handleClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="modal-btn-primary bulk-move-btn-confirm"
              disabled={!hasChanges}
              onClick={handleSubmit}
            >
              Update Items
            </button>
          </div>
        </div>
      </div>

      {/* Category Selector Modal - Rendered via Portal */}
      {showCategoryModal && createPortal(
        <CategorySelectorModal
          isOpen={showCategoryModal}
          onClose={() => setShowCategoryModal(false)}
          selectedCategoryIds={selectedCategoryIds}
          onChange={setSelectedCategoryIds}
          token={token}
          allCategories={allCategories}
        />,
        document.body
      )}
    </>,
    document.body
  );
};

export default BulkEditModal;
